/**
 * Tenant registry.
 *
 * Tenant is the top-level dimension of every report. Event tables carry it in
 * a `tenant` column whose spelling drifts (`CLIRNET`/`clirnet`, `dentnet` for
 * dentalnet), and opix also encodes it in `logName` (`opix-prod-server-<t>`).
 * The user dimension carries it as `principal_entity_slug`. `aliases` lists
 * every spelling that should count as the tenant; matching is lower-cased.
 *
 * A tenant found in ClickHouse but missing here still works: /api/tenants
 * adds it with default branding and every module enabled (see `fallbackTenant`).
 *
 * `modules` gates tabs and `labels` renames them. Both come from the tenant's
 * static config file (config/<tenant>.json, via shared/tenantConfig.js); a
 * tenant without a config file gets every module under its default name.
 *
 * `reportsOnly` limits a tenant to the existing reports: no Manage view (its
 * tabs are marked `fresh` in shared/catalog.js) and no sidebar toggle to it.
 *
 * `platforms` says where the tenant is live: `web`, `android`, `ios`. Tabs
 * tagged with a `platform` in shared/catalog.js are hidden when the tenant
 * lacks it; `app` means either app (the data does not split Android from iOS).
 *
 * `regions` says where the tenant's audience is: `in` (India) and/or `gl`
 * (global). Every tenant is India-only unless it lists `gl`; only then do the
 * country cards (world maps) and the country filter show.
 */

import { TENANT_CONFIGS } from "../config/index.js";
import { modulesFromConfig } from "./tenantConfig.js";

export const ALL_TENANTS = "all";

/** Every engagement module the app knows about. */
export const MODULES = [
  "video",
  "series",
  "medwiki",
  "session",
  "training",
  "survey",
  "epub",
  "channel",
  "reels",
  "stories",
  "podcasts",
  "trends",
  "loyalty",
];

/** Modules and display names for a tenant: from its config file, else everything. */
function fromConfig(key) {
  const cfg = TENANT_CONFIGS[key];
  return cfg ? modulesFromConfig(cfg) : { modules: MODULES, labels: {} };
}

const DEFAULT_BRAND = { primary: "#0b2545", accent: "#d64045" };

/** Platforms a tenant can be live on. */
export const PLATFORMS = ["web", "android", "ios"];

const ALL_PLATFORMS = { web: true, android: true, ios: true };
const WEB_ONLY = { web: true, android: false, ios: false };

/** Regions a tenant can serve: India, global. */
export const REGIONS = ["in", "gl"];

const INDIA_ONLY = ["in"];
const INDIA_AND_GLOBAL = ["in", "gl"];

export const TENANTS = [
  {
    key: "clirnet",
    // CLIRNET principal entity id = the auth backend's tenet_id.
    entityId: 1,
    name: "CLIRNET",
    aliases: ["clirnet"],
    brand: { primary: "#0f2c60", accent: "#0f2c60" },
    platforms: ALL_PLATFORMS,
    regions: INDIA_AND_GLOBAL,
    ...fromConfig("clirnet"),
  },
  {
    key: "dentalnet",
    // CLIRNET principal entity id = the auth backend's tenet_id.
    entityId: 75,
    name: "DentalNet",
    aliases: ["dentalnet", "dentnet"],
    brand: { primary: "#3c7abe", accent: "#3c7abe" },
    platforms: WEB_ONLY,
    regions: INDIA_ONLY,
    ...fromConfig("dentalnet"),
  },
  {
    key: "vetnet",
    // CLIRNET principal entity id = the auth backend's tenet_id.
    entityId: 40,
    name: "VetNet",
    aliases: ["vetnet"],
    brand: { primary: "#6a4b9f", accent: "#6a4b9f" },
    platforms: WEB_ONLY,
    regions: INDIA_ONLY,
    ...fromConfig("vetnet"),
  },
  {
    key: "docflix",
    // CLIRNET principal entity id = the auth backend's tenet_id.
    entityId: 123,
    name: "Docflix",
    aliases: ["docflix"],
    brand: { primary: "#0b2545", accent: "#e63946" },
    reportsOnly: true,
    platforms: WEB_ONLY,
    regions: INDIA_ONLY,
    ...fromConfig("docflix"),
  },
  {
    key: "ayushnet",
    // CLIRNET principal entity id = the auth backend's tenet_id.
    entityId: 112,
    name: "AyushNet",
    aliases: ["ayushnet"],
    brand: { primary: "#1b4332", accent: "#95d5b2" },
    platforms: WEB_ONLY,
    regions: INDIA_ONLY,
    ...fromConfig("ayushnet"),
  },
];

export const DEFAULT_TENANT = "clirnet";

const TENANT_KEY = /^[a-z0-9][a-z0-9_-]{0,39}$/;

export function isTenantKey(value) {
  return value === ALL_TENANTS || TENANT_KEY.test(String(value ?? ""));
}

export function fallbackTenant(key) {
  return {
    key,
    name: key.charAt(0).toUpperCase() + key.slice(1),
    aliases: [key],
    brand: DEFAULT_BRAND,
    modules: MODULES,
    labels: {},
    platforms: ALL_PLATFORMS,
    regions: INDIA_ONLY,
    discovered: true,
  };
}

/** Registry entry for a key, or a fallback for a tenant only seen in the data. */
export function getTenant(key) {
  return TENANTS.find((t) => t.key === key) ?? fallbackTenant(key);
}

/** The registry tenant a raw `tenant` column value belongs to (handles aliases). */
export function canonicalTenant(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  if (!v) return "";
  return TENANTS.find((t) => t.aliases.includes(v))?.key ?? v;
}

/** Lower-cased spellings to match in event tables for a tenant key. */
export function tenantAliases(key) {
  return getTenant(key).aliases;
}

/** opix `logName` values that belong to a tenant. */
export function tenantLogNames(key) {
  return tenantAliases(key).map((a) => `opix-prod-server-${a}`);
}

/** Display name of a module for a tenant, or `fallback` when the config does not rename it. */
export function moduleLabel(tenantKey, module, fallback) {
  if (!module || tenantKey === ALL_TENANTS) return fallback;
  return getTenant(tenantKey).labels?.[module] ?? fallback;
}

export function hasModule(tenantKey, module) {
  if (tenantKey === ALL_TENANTS) return true;
  return getTenant(tenantKey).modules.includes(module);
}

/** Whether a `platforms` object includes `platform`; `app` is either app. Missing → everything. */
export function platformsInclude(platforms, platform) {
  if (!platforms) return true;
  if (platform === "app") return Boolean(platforms.android || platforms.ios);
  return Boolean(platforms[platform]);
}

export function hasPlatform(tenantKey, platform) {
  if (tenantKey === ALL_TENANTS) return true;
  return platformsInclude(getTenant(tenantKey).platforms, platform);
}

/** Whether a tenant serves `region` (`in` or `gl`). Missing `regions` → India only. All tenants → any tenant does. */
export function hasRegion(tenantKey, region) {
  if (tenantKey === ALL_TENANTS) return TENANTS.some((t) => (t.regions ?? INDIA_ONLY).includes(region));
  return (getTenant(tenantKey).regions ?? INDIA_ONLY).includes(region);
}
